import axios from 'axios';
import type {
  CancellationRelativeTimes,
  OrderApiResponse,
  ProductPickUpConditions
} from 'src/libs/api/response/OrderApiResponse';
import type { OrderSchema } from 'src/models/order/schema';
import type { CartItem } from 'src/models/orderLimits/schema';
import { Left, Right } from 'src/utils/either';
import { errorMessage } from 'src/utils/errorMessage';
import { shoppingApiBaseUrl } from 'src/utils/shoppingApiBaseUrl';
import { getLocaleFromUrl, getSubdomainFromUrl } from 'src/utils/subdomain';
import { getRequest, postRequest } from '../wa/gateways/api/wamazingApi';
import type { WechatpayModalParams } from '../wechat/payment';
import { payWithWechatModal } from '../wechat/payment';
import type { ApiResponse } from './response/ApiResponse';
import type { CartPriceCheckApiResponse } from './response/CartPriceCheckApiResponse';

const fetchOrderById = async (orderId: string, isProxy?: boolean): Promise<ApiResponse<OrderApiResponse>> => {
  const response = await getRequest<{
    order: OrderApiResponse;
    errors: string[];
  }>({
    baseURI: shoppingApiBaseUrl(getLocaleFromUrl()),
    path: `/api/v1${isProxy ? '/proxy_purchase' : ''}/orders/${orderId}`
  });
  if (response.errors && response.errors.length > 0) {
    return new Left({ errors: response.errors });
  }
  return new Right(response.order);
};

const postDryrun = async (params: OrderSchema): Promise<ApiResponse<OrderApiResponse>> => {
  const response = await postRequest<{
    order: OrderApiResponse;
    errors: string[];
  }>({
    baseURI: shoppingApiBaseUrl(getLocaleFromUrl()),
    path: '/api/v1/dryrun/orders',
    params
  });
  if (response.errors && response.errors.length > 0) {
    return new Left({ errors: response.errors });
  }
  return new Right(response.order);
};

const postDryrunSecond = async (
  orderId: string,
  paymentCode: string,
  expectedPaymentAmount: number
): Promise<ApiResponse<{}>> => {
  const response = await postRequest<{
    errors: string[];
  }>({
    baseURI: shoppingApiBaseUrl(getLocaleFromUrl()),
    path: '/api/v1/payments/dryrun_second',
    params: { orderId, paymentCode, expectedPaymentAmount }
  });
  if (response.errors && response.errors.length > 0) {
    return new Left({ errors: response.errors });
  }
  return new Right(response);
};

const postFirstPayment = async (params: OrderSchema): Promise<ApiResponse<{ redirectUrl: string }>> => {
  const response = await postRequest<{
    redirectUrl: string;
    errors: string[];
  }>({
    baseURI: shoppingApiBaseUrl(getLocaleFromUrl()),
    path: '/api/v1/gmo/orders',
    params: {
      ...params,
      subdomain: getSubdomainFromUrl(window.location.hostname)
    }
  });
  if (response.errors && response.errors.length > 0) {
    return new Left({ errors: response.errors });
  }
  return new Right({ redirectUrl: response.redirectUrl });
};

const createOrderId = async (params: OrderSchema): Promise<ApiResponse<string>> => {
  const response = await postRequest<{
    orderId: string;
    errors: string[];
  }>({
    baseURI: shoppingApiBaseUrl(getLocaleFromUrl()),
    path: '/api/v1/aftee/orders',
    params
  });
  if (response.errors && response.errors.length > 0) {
    return new Left({ errors: response.errors });
  }
  return new Right(response.orderId);
};

const postFirstPaymentViaAftee = async (
  transactionId: string,
  authenticationToken: string,
  orderId: string
): Promise<ApiResponse<string>> => {
  const response = await postRequest<{
    orderId: string;
    errors: string[];
  }>({
    baseURI: shoppingApiBaseUrl(getLocaleFromUrl()),
    path: '/api/v1/aftee/orders/charge',
    params: {
      transactionId,
      authenticationToken,
      orderId
    }
  });
  if (response.errors && response.errors.length > 0) {
    return new Left({ errors: response.errors });
  }
  return new Right(response.orderId);
};

const postFirstPaymentWithWechat = async (params: OrderSchema) => {
  const response = await postRequest<{
    wechatpayJsPayReq: WechatpayModalParams;
    order: OrderApiResponse;
    errors: string[];
  }>({
    baseURI: shoppingApiBaseUrl(getLocaleFromUrl()),
    path: '/api/v1/wechatpay/orders',
    params: {
      ...params,
      subdomain: getSubdomainFromUrl(window.location.hostname)
    }
  });
  if (response.errors && response.errors.length > 0) {
    throw new Error(response.errors[0]);
  }
  payWithWechatModal(response.order.orderId, response.wechatpayJsPayReq, false);
};

const postSecondPayment = async (
  orderId: string,
  paymentCode: string,
  expectedPaymentAmount: number
): Promise<ApiResponse<boolean>> => {
  const response = await postRequest<{
    redirectUrl?: string;
    errors: string[];
  }>({
    baseURI: shoppingApiBaseUrl(getLocaleFromUrl()),
    path: '/api/v1/payments/gmo_second',
    params: { orderId, paymentCode, expectedPaymentAmount }
  });
  if (response.errors && response.errors.length > 0) {
    return new Left({ errors: response.errors });
  }
  return new Right(true);
};

const postSecondPaymentViaAftee = async (
  orderId: string,
  paymentCode: string,
  expectedPaymentAmount: number
): Promise<ApiResponse<boolean>> => {
  const response = await postRequest<{
    redirectUrl?: string;
    errors: string[];
  }>({
    baseURI: shoppingApiBaseUrl(getLocaleFromUrl()),
    path: '/api/v1/payments/aftee_second',
    params: { orderId, paymentCode, expectedPaymentAmount }
  });
  if (response.errors && response.errors.length > 0) {
    return new Left({ errors: response.errors });
  }
  return new Right(true);
};

const postSecondPaymentWithWechat = async (
  orderId: string,
  paymentCode: string,
  expectedPaymentAmount: number,
  wechatCode: string,
  isProxy: boolean
) => {
  const params = isProxy
    ? { orderId, paymentCode, expectedPaymentAmount, code: wechatCode }
    : { orderId, paymentCode, expectedPaymentAmount };

  const response = await postRequest<{
    wechatpayJsPayReq: WechatpayModalParams;
    order: OrderApiResponse;
    errors: string[];
  }>({
    baseURI: shoppingApiBaseUrl(getLocaleFromUrl()),
    path: isProxy ? '/api/v1/proxy_purchase/payments/wechatpay_without_user' : '/api/v1/payments/wechatpay_second',
    params
  });
  if (response.errors && response.errors.length > 0) {
    return new Left({ errors: response.errors });
  }
  payWithWechatModal(response.order.orderId, response.wechatpayJsPayReq, isProxy);
};

const cancelOrder = async (orderId: string, isProxy?: boolean): Promise<ApiResponse<{}>> => {
  const response = await postRequest<{
    order: OrderApiResponse;
    errors: string[];
  }>({
    baseURI: shoppingApiBaseUrl(getLocaleFromUrl()),
    path: `/api/v1${isProxy ? '/proxy_purchase' : ''}/orders/${orderId}/cancel`
  });
  if (response.errors && response.errors.length > 0) {
    return new Left({ errors: response.errors });
  }
  return new Right(response);
};

const fetchCancelPolicyRelativeTimes = async (
  shopId: number,
  productIds: number[],
  pickupDate: string,
  pickupTimeRange: string
): Promise<ApiResponse<CancellationRelativeTimes>> => {
  const response = await axios.get(
    `${shoppingApiBaseUrl(getLocaleFromUrl())}/api/v1/orders/cancel_policy_relative_times`,
    { params: { shopId, productIds, pickupDate, pickupTimeRange } }
  );
  if (!response.data) return new Left({ errors: ['no data'] });
  return new Right(response.data);
};

const fetchCartPriceCheck = async (
  shopId: number,
  lineItems: CartItem[],
  couponHistoryId?: number
): Promise<ApiResponse<CartPriceCheckApiResponse>> => {
  try {
    const response = await postRequest<CartPriceCheckApiResponse>({
      baseURI: shoppingApiBaseUrl(getLocaleFromUrl()),
      path: `/api/v1/orders/price_and_check`,
      params: {
        shopId,
        lineItems,
        couponHistoryId
      }
    });
    return new Right(response);
  } catch (e) {
    return new Left({ errors: [errorMessage(e)] });
  }
};

const fetchProductPickUpConditions = async (
  shopId: number,
  productIds: number[]
): Promise<ApiResponse<ProductPickUpConditions>> => {
  const response = await axios.get(
    `${shoppingApiBaseUrl(getLocaleFromUrl())}/api/v1/orders/product_pickup_conditions_and_order_pickup_date_times`,
    { params: { shopId, productIds } }
  );
  if (!response.data) return new Left({ errors: ['no data'] });

  return new Right(response.data);
};

export {
  cancelOrder,
  createOrderId,
  fetchCancelPolicyRelativeTimes,
  fetchCartPriceCheck,
  fetchOrderById,
  fetchProductPickUpConditions,
  postDryrun,
  postDryrunSecond,
  postFirstPayment,
  postFirstPaymentViaAftee,
  postFirstPaymentWithWechat,
  postSecondPayment,
  postSecondPaymentViaAftee,
  postSecondPaymentWithWechat
};
